import api from './api';
import {
  filterAndNormalizeDuesAccounts,
  getApiErrorMessage,
  isNotFoundError,
} from './feeOverdueDues';
import { parseOverdueDuesResponse, parseParentAccountsResponse } from './parentAccountsApi';

function normalizeApiRows(data) {
  return filterAndNormalizeDuesAccounts(data || []);
}

/**
 * Load overdue dues: primary endpoint, then parent-accounts fallback (no legacy monthly APIs).
 * @returns {Promise<{ rows: Array, meta: object, usedFallback: boolean }>}
 */
export async function fetchOverdueDuesRows(params) {
  try {
    const res = await api.get('/fees/parent-accounts/overdue-dues', { params });
    if (res.data?.success === false) {
      throw Object.assign(new Error(res.data.message || 'Failed to load overdue fee dues.'), {
        response: { status: 400, data: res.data },
      });
    }
    const fallbackPage = params?.page ?? 1;
    const { rows, meta } = parseOverdueDuesResponse(res.data?.data, fallbackPage);
    return { rows: normalizeApiRows(rows), meta, usedFallback: false };
  } catch (primaryError) {
    const tryFallback = isNotFoundError(primaryError) || primaryError.response?.status >= 500;
    if (!tryFallback) {
      throw primaryError;
    }

    try {
      const fallbackParams = { ...params };
      delete fallbackParams.page;
      delete fallbackParams.limit;
      const res = await api.get('/fees/parent-accounts', { params: fallbackParams });
      const { accounts } = parseParentAccountsResponse(res.data?.data);
      const rows = normalizeApiRows(accounts);
      const overdueTotal = rows.filter((row) => (row.status || 'Overdue') === 'Overdue').length;
      return {
        rows,
        meta: {
          total: rows.length,
          page: 1,
          limit: rows.length || 50,
          pages: 1,
          overdue_total: overdueTotal,
        },
        usedFallback: true,
      };
    } catch (fallbackError) {
      throw Object.assign(
        new Error(
          getApiErrorMessage(
            fallbackError,
            'Overdue dues API missing on server (404). Deploy latest backend or use local API.'
          )
        ),
        { response: fallbackError?.response, cause: primaryError }
      );
    }
  }
}
